export const normalizeSearch = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

// Culinary names and common spellings. These help find dishes, not certify diets.
const equivalents = [
  ['chicken', 'pollo', 'pollos', 'murgh', 'chiken', 'chikcen'],
  ['lamb', 'cordero', 'mutton', 'muton'],
  ['prawns', 'prawn', 'gamba', 'gambas', 'shrimp', 'shrimps', 'jhinga'],
  ['vegetables', 'vegetable', 'verdura', 'verduras', 'vegetales'],
  ['chickpeas', 'chickpea', 'garbanzo', 'garbanzos', 'chana'],
  ['lentils', 'lentil', 'lenteja', 'lentejas', 'dal', 'dhal', 'daal'],
  ['egg', 'eggs', 'huevo', 'huevos'],
  ['cheese', 'queso', 'paneer', 'panir'],
  ['mushroom', 'mushrooms', 'champinon', 'champinones'],
  ['spinach', 'espinaca', 'espinacas', 'palak'],
  ['potato', 'potatoes', 'patata', 'patatas', 'papa', 'papas', 'aloo'],
  ['rice', 'arroz', 'aroz'],
  ['garlic', 'ajo'], ['coconut', 'coco'], ['mint', 'hierbabuena', 'peppermint'],
  ['tikka', 'tika', 'tica'], ['masala', 'massala', 'masalla'],
  ['naan', 'nan', 'naam'], ['biryani', 'biriani', 'biriyani'],
  ['tandoori', 'tandori', 'tanduri'], ['curry', 'curries', 'curri'],
  ['samosa', 'samosas'], ['lassi', 'lasi'], ['yoghurt', 'yogurt', 'yogur'],
  ['coffee', 'cafe', 'coffe'], ['tea', 'te'], ['beer', 'cerveza', 'cervezas'],
];
const aliases = new Map();
equivalents.forEach(group => group.forEach(word => aliases.set(word, group[0])));
const canonical = word => aliases.get(word) || word;
const ignoredWords = new Set(['a', 'al', 'de', 'del', 'el', 'la', 'los', 'las', 'un', 'una', 'con', 'y', 'the', 'of', 'with', 'and']);
const words = value => normalizeSearch(value).replace(/\b(?:tica|tika|tikka)masal+a\b/g, 'tikka masala').split(/[^a-z0-9]+/).filter(word => word && !ignoredWords.has(word));

// Bounded edit distance, including adjacent swaps (e.g. "chikcen").
function distance(a, b, limit) {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  let beforePrevious;
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(row[j - 1] + 1, previous[j] + 1, previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) row[j] = Math.min(row[j], beforePrevious[j - 2] + 1);
    }
    if (Math.min(...row) > limit) return limit + 1;
    beforePrevious = previous;
    previous = row;
  }
  return previous[b.length];
}

export function createMenuSearch(items, categories, proteins) {
  const vocabulary = new Set();
  const index = items.map(item => {
    const tokens = new Map();
    const add = (value, weight) => {
      if (!value) return;
      words(typeof value === 'string' ? value : value.es + ' ' + value.en).forEach(word => {
        [word, canonical(word)].forEach(token => {
          tokens.set(token, Math.max(tokens.get(token) || 0, weight));
          vocabulary.add(token);
        });
      });
    };
    add(item.name, 6);
    add(item.description, 2);
    add(item.group, 1);
    add(categories.find(category => category.id === item.category).name, 1);
    if (item.category === 'curry') proteins.forEach(protein => add(protein.name, 3));
    if (item.category === 'biryani') add('arroz rice', 3);
    if (item.category === 'naan') add('pan bread', 3);
    return { item, tokens };
  });
  const dictionary = [...vocabulary];
  let lastQuery;
  let lastResult = [];
  return query => {
    const normalized = normalizeSearch(query);
    if (normalized === lastQuery) return lastResult;
    lastQuery = normalized;
    const terms = [...new Set(words(normalized).map(canonical))];
    const alternatives = terms.map(term => {
      // Known words stay exact: "chicken" must never become "chickpeas".
      if (vocabulary.has(term)) return [[term, 0]];
      const prefixes = term.length >= 3 ? dictionary.filter(token => token.indexOf(term) === 0).map(token => [token, 0.5]) : [];
      if (prefixes.length) return prefixes;
      if (term.length < 4) return [];
      const limit = term.length >= 7 ? 2 : 1;
      return dictionary.map(token => [token, distance(term, token, limit)]).filter(pair => pair[1] <= limit);
    });
    if (!terms.length || alternatives.some(options => !options.length)) return (lastResult = []);
    lastResult = index.map(entry => {
      const scores = alternatives.map(options => options.reduce((best, [token, cost]) => Math.max(best, (entry.tokens.get(token) || 0) / (1 + cost)), 0));
      return { item: entry.item, score: scores.every(Boolean) ? scores.reduce((sum, score) => sum + score, 0) : 0 };
    }).filter(entry => entry.score > 0).sort((a, b) => b.score - a.score).map(entry => entry.item);
    return lastResult;
  };
}



// WEBPACK FOOTER //
// ./src/data/menuSearch.js